/**
 * The custom GA4 events for section 4 of the reporting brief — "Page
 * Interactions, Journey & Events".
 *
 * Everything in sections 1–3 of that brief (sessions, engagement rate, bounce
 * rate, users, devices, channels, source/medium/campaign, new vs returning,
 * previous path) is collected by GA4 itself from the `page_view` that
 * `lib/analytics.ts` already sends. NOTHING in this file is needed for those,
 * and no event here should ever try to recompute one of them — see
 * README "Analytics events" for which report row comes from where.
 *
 * What GA4 cannot see on its own is what a visitor DOES on the page, so this
 * module adds exactly that, and only from the page's own markup:
 *
 *   - `scroll_depth`     25 / 50 / 75 / 90 % of the page reached, once each
 *   - `cta_click`        the two "Donate & Enter" buttons
 *   - `link_click`       every other anchor, internal or outbound
 *   - `form_start`       first focus in the donation form
 *   - `section_view`     each section reaching the middle of the viewport
 *   - `video_*`          start / progress / pause / complete on the hero video
 *
 * The donation flow itself (`donate_submit`, `checkout_open`,
 * `donation_complete`, `checkout_cancel`) is emitted from `lib/donate-flow.ts`,
 * because those moments are only observable inside the two-vendor chain.
 *
 * The brief suggests Google Tag Manager for this. These events are sent straight
 * to GA4, not through a GTM tag: they arrive as ordinary events and can be
 * marked as key events in the GA4 UI without a deploy. `trackEvent` also mirrors
 * each one onto the dataLayer, so the landing page's container
 * (lib/tag-manager.ts) can trigger other vendors' tags on them.
 *
 * There is no consent gate here, matching `initAnalytics`. If one is ever added
 * it belongs in that module, above the `config` call, and this file inherits it
 * for free — every event goes out through `trackEvent`.
 */
import { trackEvent } from "./analytics";

/**
 * Depth marks, in percent of the page.
 *
 * Read the GA4 way: the point is reached when the BOTTOM of the viewport passes
 * it, which is how GA4's own enhanced-measurement `scroll` event defines 90%.
 * Same definition, so the two are directly comparable — different event name, so
 * they are never double counted.
 */
const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;

/** Quarter marks for the hero video, plus 10% to separate a real view from a scrub. */
const VIDEO_THRESHOLDS = [10, 25, 50, 75] as const;

/** Longest link/section label kept. GA4 truncates parameter values at 100 chars. */
const MAX_TEXT = 100;

/**
 * The GoFundMe checkout trigger. It is a real anchor only because their SDK has
 * no imperative open API (see donate.ts), and the click on it is ours, not the
 * visitor's — counting it would report a phantom link click on top of the
 * `checkout_open` that donate-flow already sends for the same moment.
 */
const SYNTHETIC_LINK = ".valvoline-donate__checkout";

/** The two donate CTAs, the button whose click-through is the headline KPI. */
const CTA_LINKS = ".valvoline-nav__cta, .valvoline-hero__cta";

/** The poster variant of the hero video — an anchor when `video-href` is set. */
const VIDEO_LINK = ".valvoline-hero__video";

function text(node: Element | null): string {
  return (node?.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, MAX_TEXT);
}

/**
 * Which section the element sits in, as a bare name (`hero`, `donate`, …).
 *
 * Derived from the class prefix every rule in this project already carries
 * (CLAUDE.md — "prefix every CSS class with `valvoline-<section>`"), so a new
 * section is labelled correctly the moment it exists, with nothing to register
 * here. `valvoline-hero__cta` and `valvoline-hero` both answer "hero".
 */
function sectionOf(node: Element | null): string {
  for (let el = node; el; el = el.parentElement) {
    for (const token of el.classList) {
      const match = /^valvoline-([a-z][a-z-]*?)(?:__|--|$)/.exec(token);
      if (match) return match[1];
    }
  }
  return "";
}

/** The href as an absolute URL, or null if it is not one (`mailto:`, `#`, junk). */
function resolve(href: string): URL | null {
  try {
    return new URL(href, location.href);
  } catch {
    return null;
  }
}

/** How far down the page the bottom of the viewport has reached, 0–100. */
function scrolledPercent(): number {
  const doc = document.documentElement;
  const height = Math.max(doc.scrollHeight, document.body?.scrollHeight ?? 0);
  if (height <= 0) return 0;
  const bottom = window.scrollY + window.innerHeight;
  return Math.min(100, Math.round((bottom / height) * 100));
}

/**
 * Scroll depth. One event per mark per page load, never re-fired.
 *
 * Measured on a rAF so a flick of the wheel is one measurement rather than
 * thirty, and evaluated once up front too: a page that already fits the viewport
 * has genuinely been scrolled through, and reporting nothing for it would make
 * every short viewport look like a bounce with no depth at all.
 */
function trackScrollDepth(): () => void {
  const fired = new Set<number>();
  let queued = false;

  const measure = (): void => {
    queued = false;
    const percent = scrolledPercent();
    for (const mark of SCROLL_THRESHOLDS) {
      if (percent < mark || fired.has(mark)) continue;
      fired.add(mark);
      trackEvent("scroll_depth", { percent_scrolled: mark });
    }
    if (fired.size === SCROLL_THRESHOLDS.length) stop();
  };

  const onScroll = (): void => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  };

  const stop = (): void => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    window.removeEventListener("load", onScroll);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  // The first measurement waits for `load`, and that is not caution about a
  // frame or two: at DOM-ready the images have no intrinsic size yet, so the
  // document is barely taller than the viewport and the bottom of the viewport
  // is already "at 90%". Measuring then reports all four marks to every visitor
  // before they have scrolled at all — which is exactly what the first run of
  // this did. Once the images are laid out the height is real.
  if (document.readyState === "complete") onScroll();
  else window.addEventListener("load", onScroll, { once: true });

  return stop;
}

/**
 * Every anchor click on the page, split into the CTA (its own event, because it
 * is the KPI) and everything else.
 *
 * Listens in the capture phase on purpose: the root element intercepts same-page
 * hash clicks to scroll smoothly, and GoFundMe's SDK swallows the click on its
 * trigger. Capture runs before either, so a hijacked click is still counted.
 *
 * Modified clicks (⌘/ctrl/shift-click, middle click) are counted too — opening
 * the rules in a new tab is the same interaction as opening it in this one. Only
 * the secondary button is ignored, since a context menu is not a click-through.
 */
function trackLinkClicks(): () => void {
  const onClick = (event: MouseEvent): void => {
    if (event.button > 1) return;

    const link = (event.target as Element | null)?.closest?.("a[href]");
    if (!link || link.matches(SYNTHETIC_LINK)) return;

    const href = link.getAttribute("href") ?? "";
    // Resolved so a relative href still reports a domain, and so the outbound
    // test is a comparison of origins rather than of string shapes. `URL.parse`
    // would say this in one line, but it is a 2024 API and this page is public,
    // so the throwing constructor it is.
    const url = resolve(href);
    const outbound = url ? url.origin !== location.origin : false;

    const name = link.matches(CTA_LINKS)
      ? "cta_click"
      : link.matches(VIDEO_LINK)
        ? "video_click"
        : "link_click";

    trackEvent(name, {
      link_url: url?.href ?? href,
      link_domain: url?.hostname,
      link_text: text(link) || link.getAttribute("aria-label") || "",
      section: sectionOf(link),
      outbound,
    });
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

/**
 * First interaction with the donation form, GA4's recommended `form_start`.
 *
 * Delegated from the document and keyed off our own wrapper class, because the
 * fields are Probability's: they are rendered by their script whenever it
 * finishes loading and replaced wholesale when the form changes state, so
 * nothing can be bound to them directly. We never read a value — only that a
 * field was focused.
 *
 * `form_submit` is NOT emitted here. Their submit button lives in the same
 * subtree, but a click on it means "validation is about to run", not "the form
 * was submitted" — donate-flow sends `donate_submit` from the seam where the
 * submission is actually real.
 */
function trackFormStart(): () => void {
  let sent = false;

  const onFocus = (event: FocusEvent): void => {
    if (sent) return;
    const field = (event.target as Element | null)?.closest?.("input, select, textarea");
    if (!field?.closest(".valvoline-donate__form")) return;

    sent = true;
    trackEvent("form_start", {
      form_id: "probability-universal-form",
      form_name: "Donate & Enter",
      section: "donate",
    });
    document.removeEventListener("focusin", onFocus, true);
  };

  document.addEventListener("focusin", onFocus, true);
  return () => document.removeEventListener("focusin", onFocus, true);
}

/**
 * Page-level tracking, for BOTH pages: the landing page and /officialrules.
 *
 * Called from the analytics entry rather than from the component, so the rules
 * page — an exported Google Doc with no component on it — still reports scroll
 * depth and its links. The form and CTA selectors simply never match there.
 */
export function startPageTracking(): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};

  const stops = [trackScrollDepth(), trackLinkClicks(), trackFormStart()];
  return () => stops.forEach((stop) => stop());
}

/**
 * Which sections were actually looked at, in order — the "journey" half of the
 * brief, and the only way to tell a visitor who read the page from one who
 * bounced off the hero.
 *
 * The trigger is the middle half of the viewport (`rootMargin` insets, threshold
 * 0), not a visible fraction: a section taller than the viewport can never be
 * 50% visible, so a ratio threshold would silently never fire for the very
 * sections that matter most on mobile.
 *
 * One event per section per page load — the observer stops watching a section
 * once it has reported, so scrolling back up does not inflate the count.
 */
function trackSectionViews(root: HTMLElement): () => void {
  if (typeof IntersectionObserver === "undefined") return () => {};

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        trackEvent("section_view", { section: sectionOf(entry.target) });
      }
    },
    { rootMargin: "-25% 0px -25% 0px", threshold: 0 },
  );

  // Sections are the root element's own children, each carrying its
  // `valvoline-<name>` class — so this picks up a newly added section without
  // anyone remembering to list it here.
  for (const child of root.children) {
    if (sectionOf(child)) observer.observe(child);
  }

  return () => observer.disconnect();
}

/**
 * Percent watched, or undefined while the duration is still unknown.
 *
 * `duration` is NaN before `loadedmetadata` and Infinity for a live stream;
 * isFinite rejects both, and 0 would divide. Callers must treat undefined as
 * "do not report yet" rather than 0 — see `onTimeUpdate`.
 */
function percentOf(el: HTMLVideoElement): number | undefined {
  return Number.isFinite(el.duration) && el.duration > 0
    ? Math.round((el.currentTime / el.duration) * 100)
    : undefined;
}

/** The shared `video_*` parameters, read off the element at the moment of the event. */
function videoDetails(el: HTMLVideoElement) {
  return {
    video_provider: "self",
    video_title: el.getAttribute("title") ?? "",
    // currentSrc, not the src attribute: it is the resolved absolute URL, and it
    // is what the browser actually fetched.
    video_url: el.currentSrc || el.src || "",
    video_current_time: Math.round(el.currentTime),
    video_duration: Number.isFinite(el.duration) ? Math.round(el.duration) : undefined,
  };
}

/** The hero video an event came from, or null when it came from anything else. */
function mediaTarget(event: Event): HTMLVideoElement | null {
  const el = event.target as Element | null;
  return el?.matches?.("video.valvoline-hero__player") ? (el as HTMLVideoElement) : null;
}

/**
 * The hero video, through the native media events on our own `<video>`.
 *
 * Simpler than the Vimeo postMessage listener it replaced (in git history, if an
 * embed ever comes back): the element is in our document, so there is no origin
 * to check, no subscription handshake, and no waiting for an iframe to leave
 * `about:blank`.
 *
 * Three things it has to work around, all of them consequences of the events
 * being the browser's rather than a player's:
 *
 * 1. **The element does not exist yet on the first render in every case, and it
 *    is replaced when `video-src` changes.** The listeners are therefore
 *    attached to `root` in the capture phase — media events do not bubble, so
 *    capture is the only way one handler covers whatever element is there.
 * 2. **There is no `percent` in the payload.** Vimeo sent one; here it is
 *    computed from `currentTime / duration`, and `duration` is `NaN` until the
 *    metadata loads. Every read guards for that — a NaN percentage would push
 *    all four thresholds at once on the first tick.
 * 3. **`timeupdate` fires 4-5 times a second**, where Vimeo's `playProgress`
 *    was throttled. The `passed` set already makes each threshold report once,
 *    which is what keeps this from flooding GA4.
 *
 * When the hero is showing a poster instead (`video-src=""` and
 * `video-embed=""`), no video element exists and the media half does nothing;
 * the click on the poster is reported as `video_click` by the link handler, and
 * the play button dispatches `valvoline-hero:play`, which is picked up below.
 */
function trackVideo(root: HTMLElement): () => void {
  const passed = new Set<number>();
  let started = false;

  const onPlaying = (event: Event): void => {
    const el = mediaTarget(event);
    if (!el) return;
    // Once per page load: a resume after a pause is the same view, and counting
    // it would make video_start outnumber the people who watched.
    if (started) return;
    started = true;
    trackEvent("video_start", videoDetails(el));
  };

  const onTimeUpdate = (event: Event): void => {
    const el = mediaTarget(event);
    if (!el) return;
    const percent = percentOf(el);
    if (percent === undefined) return;
    for (const mark of VIDEO_THRESHOLDS) {
      if (percent < mark || passed.has(mark)) continue;
      passed.add(mark);
      trackEvent("video_progress", { ...videoDetails(el), video_percent: mark });
    }
  };

  const onPause = (event: Event): void => {
    const el = mediaTarget(event);
    if (!el) return;
    // The browser fires `pause` on the way to `ended` as well. That one is a
    // completion, not an abandonment, and reporting both would make every
    // finished view look like a drop-off at 100%.
    if (el.ended) return;
    trackEvent("video_pause", { ...videoDetails(el), video_percent: percentOf(el) });
  };

  const onEnded = (event: Event): void => {
    const el = mediaTarget(event);
    if (!el) return;
    trackEvent("video_complete", { ...videoDetails(el), video_percent: 100 });
  };

  // The poster's play button, for the no-video variant. The event is the one the
  // hero already dispatches; nothing new is added to the section for analytics.
  const onPlay = (): void => {
    if (started) return;
    started = true;
    trackEvent("video_start", { video_provider: "poster", video_title: "", video_url: "" });
  };

  // Capture, because media events do not bubble — see (1) above.
  root.addEventListener("playing", onPlaying, true);
  root.addEventListener("timeupdate", onTimeUpdate, true);
  root.addEventListener("pause", onPause, true);
  root.addEventListener("ended", onEnded, true);
  root.addEventListener("valvoline-hero:play", onPlay);

  return () => {
    root.removeEventListener("playing", onPlaying, true);
    root.removeEventListener("timeupdate", onTimeUpdate, true);
    root.removeEventListener("pause", onPause, true);
    root.removeEventListener("ended", onEnded, true);
    root.removeEventListener("valvoline-hero:play", onPlay);
  };
}

/**
 * Tracking that needs the rendered page: section views and the hero video.
 *
 * Called from the root element's `firstUpdated`, which is the first moment the
 * sections and the iframe exist. Returns a teardown for `disconnectedCallback`,
 * like the other wiring there.
 */
export function startComponentTracking(root: HTMLElement): () => void {
  const stops = [trackSectionViews(root), trackVideo(root)];
  return () => stops.forEach((stop) => stop());
}
